import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload, faSpinner, faCheckCircle, faTimesCircle, faRobot,
  faTrash, faCheck, faFileAlt, faExclamationTriangle, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import ConfirmModal from './ConfirmModal';

const CATEGORY_STYLE = {
  offre_financiere:     { label: 'Offre Financière',     color: '#059669', bg: '#d1fae5' },
  offre_technique:      { label: 'Offre Technique',      color: '#d97706', bg: '#fef7cd' },
  dossier_administratif:{ label: 'Dossier Administratif', color: '#1967d2', bg: '#e8f0fe' },
  dossier_technique:    { label: 'Dossier Technique',    color: '#7c3aed', bg: '#f3e8ff' },
  dossier_financier:    { label: 'Dossier Financier',    color: '#dc2626', bg: '#fef2f2' },
  dossier_additif:      { label: 'Dossier Additif',      color: '#0891b2', bg: '#e0f7fa' },
};

function getCategoryStyle(cat) {
  return CATEGORY_STYLE[cat] || { label: cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, ' '), color: '#757575', bg: '#f5f5f5' };
}

const AOTab = ({ projectId, readOnly }) => {
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [editableItems, setEditableItems] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [cachedResults, setCachedResults] = useState(null);
  const [loadingCache, setLoadingCache] = useState(true);
  const abortRef = useRef(null);

  // Cancel any pending analysis on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.cancel('Component unmounted');
    };
  }, []);

  // Load cached results on mount
  useEffect(() => {
    api.get(`/projects/${projectId}/ao-analysis`)
      .then(res => {
        if (res.data.cached && res.data.data && res.data.data.length > 0) {
          const items = res.data.data;
          setCachedResults(items);
          // Auto-load into editable view
          setResults(items);
          setEditableItems(items.map((item, i) => ({ ...item, id: i, included: true })));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCache(false));
  }, [projectId]);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selected]);
    setError(null);
    setSuccessMsg(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf', 'doc', 'docx', 'txt'].includes(ext);
    });
    if (dropped.length > 0) {
      setFiles(prev => [...prev, ...dropped]);
      setError(null);
    }
  }, []);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    if (files.length === 0) return;
    setAnalyzing(true);
    setError(null);
    setSuccessMsg(null);

    // Cancel any previous request
    if (abortRef.current) abortRef.current.cancel('New analysis started');
    const source = axios.CancelToken.source();
    abortRef.current = source;

    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files[]', f));
      const res = await api({
        method: 'post',
        url: `/projects/${projectId}/ao-analyze`,
        data: fd,
        timeout: 180000,
        headers: { 'Content-Type': 'multipart/form-data' },
        cancelToken: source.token,
      });
      const items = res.data.data || [];
      setResults(items);
      setCachedResults(items);
      setEditableItems(items.map((item, i) => ({ ...item, id: i, included: true })));
    } catch (err) {
      if (axios.isCancel(err)) return; // silently ignore cancelled requests
      const msg = err.code === 'ECONNABORTED'
        ? 'L\'analyse a pris trop de temps. Veuillez réessayer.'
        : (err.response?.data?.message || 'Erreur lors de l\'analyse. Veuillez réessayer.');
      setError(msg);
      if (cachedResults && cachedResults.length > 0 && !results) {
        setResults(cachedResults);
        setEditableItems(cachedResults.map((item, i) => ({ ...item, id: i, included: true })));
      }
    } finally {
      setAnalyzing(false);
      abortRef.current = null;
    }
  };

  const toggleItem = (id) => {
    setEditableItems(prev => prev.map(item =>
      item.id === id ? { ...item, included: !item.included } : item
    ));
  };

  const updateItem = (id, field, value) => {
    setEditableItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const [newItemInputs, setNewItemInputs] = useState({}); // { category: 'name text' }

  const addItemToCategory = (cat) => {
    const name = (newItemInputs[cat] || '').trim();
    if (!name) return;
    const newId = Date.now();
    setEditableItems(prev => [...prev, { id: newId, name, description: '', category: cat, required: true, included: true }]);
    setNewItemInputs(prev => ({ ...prev, [cat]: '' }));
  };

  const handleConfirm = async () => {
    const toSave = editableItems.filter(item => item.included);
    if (toSave.length === 0) return;

    setConfirming(true);
    setError(null);
    try {
      const res = await api.post(`/projects/${projectId}/ao-checklist`, {
        items: toSave.map(({ name, description, category, required }) => ({ name, description, category, required })),
      });
      setSuccessMsg(res.data.message);
      setResults(null);
      setEditableItems([]);
      setCachedResults(null);
      setFiles([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la confirmation');
    } finally {
      setConfirming(false);
    }
  };

  const grouped = {};
  editableItems.forEach(item => {
    const cat = item.category || 'autre';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });

  return (
    <div>
      {/* Error */}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
          padding: '10px 14px', marginBottom: 16, fontSize: '0.78rem', color: '#991b1b',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </div>
      )}

      {/* Success */}
      {successMsg && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
          padding: '10px 14px', marginBottom: 16, fontSize: '0.78rem', color: '#166534',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FontAwesomeIcon icon={faCheckCircle} /> {successMsg}
        </div>
      )}

      {/* Upload section */}
      {!readOnly && !results && (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <FontAwesomeIcon icon={faRobot} style={{ color: '#dc2626' }} />
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#242424' }}>Analyse du CPS</span>
          </div>

          <p style={{ fontSize: '0.78rem', color: '#757575', margin: '0 0 16px', lineHeight: 1.5 }}>
            Importez le CPS (Cahier des Prescriptions Spéciales) et l'IA analysera le document pour extraire la liste des pièces à fournir.
          </p>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? '#dc2626' : '#e0e0e0'}`,
              borderRadius: 10, padding: '28px 20px', textAlign: 'center',
              background: dragOver ? '#fef2f2' : '#fafafa',
              transition: 'all 0.15s', cursor: 'pointer', marginBottom: 16,
            }}
            onClick={() => document.getElementById('ao-file-input').click()}
          >
            <FontAwesomeIcon icon={faUpload} style={{ fontSize: '1.3rem', color: dragOver ? '#dc2626' : '#bdbdbd', marginBottom: 8 }} />
            <div style={{ fontSize: '0.82rem', color: '#757575', fontWeight: 500 }}>
              Glissez vos fichiers ici ou cliquez pour sélectionner
            </div>
            <div style={{ fontSize: '0.68rem', color: '#bdbdbd', marginTop: 4 }}>
              PDF, Word (.doc, .docx), Texte (.txt) · Max 15 MB par fichier
            </div>
            <input
              id="ao-file-input"
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {files.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  background: '#f9fafb', borderRadius: 6, marginBottom: 4,
                }}>
                  <FontAwesomeIcon icon={faFileAlt} size="sm" style={{ color: '#9e9e9e' }} />
                  <span style={{ flex: 1, fontSize: '0.78rem', color: '#424242', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  <span style={{ fontSize: '0.65rem', color: '#bdbdbd' }}>
                    {(f.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                  <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', color: '#bdbdbd', cursor: 'pointer', padding: 2 }}>
                    <FontAwesomeIcon icon={faTrash} size="xs" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Analyze button */}
          <button
            onClick={handleAnalyze}
            disabled={files.length === 0 || analyzing}
            style={{
              width: '100%', padding: '11px 0', borderRadius: 8, border: 'none',
              background: files.length > 0 && !analyzing ? '#dc2626' : '#e5e7eb',
              color: files.length > 0 && !analyzing ? '#fff' : '#9e9e9e',
              fontSize: '0.85rem', fontWeight: 600, cursor: files.length > 0 ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.15s',
            }}
          >
            {analyzing ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Analyse en cours...</>
            ) : (
              <><FontAwesomeIcon icon={faRobot} /> Analyser le CPS</>
            )}
          </button>
        </div>
      )}

      {/* Results — review before confirming */}
      {results && editableItems.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 24px', borderBottom: '1px solid #f0f0f0',
          }}>
            <div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#242424' }}>
                Pièces du dossier
              </div>
              <div style={{ fontSize: '0.72rem', color: '#9e9e9e', marginTop: 2 }}>
                {editableItems.filter(i => i.included).length} sur {editableItems.length} sélectionnés
              </div>
            </div>
            {!readOnly && (
              <button onClick={() => { setResults(null); setEditableItems([]); }}
                style={{ fontSize: '0.72rem', padding: '5px 14px', borderRadius: 6, border: '1px solid #e0e0e0', background: '#fff', color: '#757575', cursor: 'pointer' }}>
                Ré-analyser
              </button>
            )}
          </div>

          {/* Category tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', overflowX: 'auto' }}>
            {Object.keys(grouped).map(cat => {
              const items = grouped[cat] || [];
              if (items.length === 0) return null;
              const cs = getCategoryStyle(cat);
              return (
                <div key={cat} style={{
                  flex: 1, padding: '10px 0', textAlign: 'center',
                  borderBottom: `2px solid ${cs.color}`, background: cs.bg + '40',
                }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: cs.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {cs.label}
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: cs.color, marginTop: 2 }}>
                    {items.filter(i => i.included).length}/{items.length}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Items list */}
          <div style={{ padding: '16px 24px' }}>
            {Object.keys(grouped).map((cat, catIdx) => {
              const items = grouped[cat];
              if (!items || items.length === 0) return null;
              const cs = getCategoryStyle(cat, catIdx);

              return (
                <div key={cat} style={{ marginBottom: 16 }}>
                  <div style={{
                    fontSize: '0.68rem', fontWeight: 700, color: cs.color, textTransform: 'uppercase',
                    letterSpacing: '0.04em', padding: '8px 0 6px',
                    borderBottom: `1px solid ${cs.bg}`,
                    marginBottom: 8,
                  }}>
                    {cs.label}
                  </div>

                  {items.map(item => (
                    <div key={item.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
                      borderBottom: '1px solid #f8f8f8',
                      opacity: item.included ? 1 : 0.4,
                      transition: 'opacity 0.15s',
                    }}>
                      <div
                        onClick={() => toggleItem(item.id)}
                        style={{
                          width: 18, height: 18, borderRadius: 4, flexShrink: 0, cursor: 'pointer', marginTop: 2,
                          border: item.included ? 'none' : '2px solid #d1d5db',
                          background: item.included ? '#dc2626' : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.12s',
                        }}
                      >
                        {item.included && <FontAwesomeIcon icon={faCheck} size="xs" style={{ color: '#fff', fontSize: '0.5rem' }} />}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <input
                          type="text"
                          value={item.name}
                          onChange={e => updateItem(item.id, 'name', e.target.value)}
                          style={{
                            width: '100%', fontSize: '0.82rem', fontWeight: 600, color: '#242424',
                            border: 'none', outline: 'none', background: 'transparent', padding: 0,
                          }}
                        />
                        {item.description && (
                          <div style={{ fontSize: '0.7rem', color: '#9e9e9e', marginTop: 2, lineHeight: 1.4 }}>
                            {item.description}
                          </div>
                        )}
                      </div>

                      <span
                        onClick={() => updateItem(item.id, 'required', !item.required)}
                        style={{
                          fontSize: '0.58rem', fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                          cursor: 'pointer', flexShrink: 0, transition: 'all 0.12s', marginTop: 2,
                          background: item.required ? '#fef2f2' : '#f5f5f5',
                          color: item.required ? '#dc2626' : '#9e9e9e',
                        }}
                      >
                        {item.required ? 'Obligatoire' : 'Optionnel'}
                      </span>
                    </div>
                  ))}

                  {/* Add item to this category */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                    <input
                      type="text"
                      value={newItemInputs[cat] || ''}
                      onChange={e => setNewItemInputs(prev => ({ ...prev, [cat]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') addItemToCategory(cat); }}
                      placeholder="Ajouter un élément..."
                      style={{ flex: 1, fontSize: '0.75rem', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none' }}
                    />
                    <button
                      onClick={() => addItemToCategory(cat)}
                      disabled={!(newItemInputs[cat] || '').trim()}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: 'none',
                        background: (newItemInputs[cat] || '').trim() ? '#dc2626' : '#e5e7eb',
                        color: (newItemInputs[cat] || '').trim() ? '#fff' : '#9ca3af',
                        cursor: 'pointer', fontSize: '0.72rem',
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} size="xs" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer actions */}
          {!readOnly && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 24px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
              <button onClick={() => { setResults(null); setEditableItems([]); }}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#424242', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={handleConfirm} disabled={confirming || editableItems.filter(i => i.included).length === 0}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: '#dc2626', color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                  opacity: confirming ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {confirming ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
                {confirming ? 'Enregistrement...' : `Confirmer (${editableItems.filter(i => i.included).length})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AOTab;
